import { Transaction } from '@web/walter-lib/stream/types'
import { FormattedHTMLMessage } from '@web/walter-ui-kit'
import React from 'react'
import { HTMLMessageDescriptor } from '@web/walter-lib/react-intl'
import TxNotificationIcon from '../../TxNotificationIcon'
import TxPublicMessage from '../../TxPublicMessage'
import styles from '../style.module.css'
import { ProcessedNotificationData } from '../types'
import { getErrorNotification } from '../notifications'
import AmountContent from '../AmountContent'
import { lang } from './lang'

// eslint-disable-next-line complexity
export const processDeposit = (data: Transaction): ProcessedNotificationData | null => {
    /**
     * Data subtype usage:
     * UNCLASSIFIED: swap flow
     * BLOCKCHAIN: deposit (receive) on chain
     * OFFCHAIN: internal deposit (receive) from broker user without chain tx
     * SUB_ACCOUNT: deposit (receive) from sub account
     * FIAT: deposit (receive) fiat
     * BUY_CRYPTO: buy crypto deposit
     * STAKING: staking reward deposit
     * AFFILIATE: referral reward deposit
     * AIRDROP: airdrop reward deposit
     */

    /**
     * Currently only success and failed statuses processed
     * Pending and created statuses are processed as "pending" one-time notification triggered on REST request
     */
    if (data.status === 'SUCCESS') {
        /**
         * Success
         */
        const message = data.native?.public_comment

        let icon = <TxNotificationIcon status="SUCCESS" />
        let titleLang: HTMLMessageDescriptor
        let amountLang: HTMLMessageDescriptor
        let amount = data.native?.amount
        let currency = data.native?.currency

        if (data.subtype === 'STAKING') {
            icon = <TxNotificationIcon status="SUCCESS" subtype="STAKING" />
            titleLang = lang.successStakingTitle
            amountLang = lang.successStakingContent
        } else if (data.subtype === 'AFFILIATE') {
            icon = <TxNotificationIcon status="SUCCESS" subtype="AFFILIATE" />
            titleLang = lang.successReferralTitle
            amountLang = lang.successReferralContent
        } else if (data.subtype === 'AIRDROP') {
            icon = <TxNotificationIcon status="SUCCESS" subtype="AIRDROP" />
            titleLang = lang.successAirdropTitle
            amountLang = lang.successAirdropContent
        } else if (data.subtype === 'BUY_CRYPTO') {
            if (data?.meta?.fiat_to_crypto?.status === 'COMPLETED') {
                titleLang = lang.successBuyTitle
                amountLang = lang.successBuyContent
                amount = data.native?.amount || data.meta?.fiat_to_crypto?.target_amount
                currency = data.native?.currency || data.meta?.fiat_to_crypto?.target_currency
            } else {
                return null
            }
        } else if (data.subtype !== 'UNCLASSIFIED') {
            titleLang = lang.successReceiveTitle
            amountLang = lang.successReceiveContent
        } else {
            return null
        }

        return {
            icon,
            title: <FormattedHTMLMessage {...titleLang} />,
            content: (
                <>
                    <AmountContent title={amountLang} amount={amount || '0'} sign={1} currencyCode={currency || ''} />
                    {message && (
                        <>
                            <div className={styles.divider} />
                            <TxPublicMessage message={message} />
                        </>
                    )}
                </>
            ),
            status: 'success',
            dataPointer: 'deposit_success_notification',
        }
    } else if (data.status === 'FAILED') {
        /**
         * Failed
         */
        if (data.subtype === 'BUY_CRYPTO') {
            return getErrorNotification({
                title: lang.errorBuyTitle,
                content: lang.errorBuyContent,
                dataPointer: 'deposit_failed_notification',
            })
        }

        return null
    }

    return null
}
